import { divProperties, FontIcon, IPanelProps, mergeStyles, MessageBar, MessageBarType, Panel, PanelType, Stack, Text, useTheme } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import * as React from 'react';
import { IInvoice } from '../../../../Models/Invoice/IInvoice';
import { IInvoiceItem } from '../../../../Models/Invoice/IInvoiceItem';
import { IStatusMessage } from '../../../../Models/IStatusMessage';
import { IInvoiceApi } from '../../../../Services/API/Invoice/IInvoiceApi';
import { InvoiceDetailView } from './InvoiceDetailView';
import * as Services from '../../../../Services/Services';
import { IUserListItem } from '../../../../Models/Admin/IUserListItem';
import { IAdminApi } from '../../../../Services/API/Admin/IAdminApi';
import { IContactInfoApi } from '../../../../Services/API/ContactInfo/IContactInfoApi';
import { dateToStr } from '../../../../Tools/DateTime';
import { StornoStateEnum } from '../../../../Models/Invoice/StornoStateEnum';

interface IInvoiceDetailProps {
    isDetailOpened: boolean;
    onDismiss: () => void;    
    refresh?: () => void;
    invoiceId?: number;
}


export const InvoiceDetail: React.FunctionComponent<IInvoiceDetailProps> = (props) => {

    const [originalInvoice, setOriginalInvoice] = React.useState<IInvoice>()

    const [isLoading, setIsLoading] = useBoolean(false)
    const [invoice, setInvoice] = React.useState<IInvoice>()
    const [statusMessage, setStatusMessage] = React.useState<IStatusMessage>()
    const [isReadOnly, setReadOnly] = React.useState(true);
    const [isChanged, setIsChanged] = React.useState(false);

    const theme = useTheme();
    const iconClass = mergeStyles({
        fontSize: 50,
        height: 50,
        width: 50,
        margin: '0 25px',
        color: theme.palette.themePrimary
    });
    const getInvoiceDetail = async (invoiceId: number) => {
        setIsLoading.setTrue();
        setStatusMessage(undefined);
        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.getInvoice(invoiceId);
        if (result.isSuccess && result.data) {
            setStatusMessage({ message: result.message, type: MessageBarType.success });
            setInvoice(result.data);
            setOriginalInvoice(result.data);
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    React.useEffect(() => {
        if (props.invoiceId !== undefined) {
            if (props.invoiceId > 0) {
                getInvoiceDetail(props.invoiceId);
            }
            else {
                createNewInvoice();
            }
            setReadOnly(false)
        }
        else {
            setReadOnly(true)
        }


    }, [props.invoiceId]);

    React.useEffect(() => {
        if (originalInvoice && invoice) {

            const changed = (originalInvoice.city === invoice.city ||
                originalInvoice.street === invoice.street ||
                originalInvoice.postalCode === invoice.postalCode ||
                originalInvoice.name === invoice.name ||
                originalInvoice.ic === invoice.ic ||
                originalInvoice.dic === invoice.dic ||
                originalInvoice.due === invoice.due ||
                originalInvoice.variabil === invoice.variabil ||
                originalInvoice.uskut === invoice.uskut
            );
            setIsChanged(changed);
        }


    }, [invoice]);

    const createNewInvoice = async () => {

        //        Stanout konfiguraci a vypnit default hodnoty

        const infoApi = Services.ServicesProvider.GetService<IContactInfoApi>(Services.ContactInfoApi);
        let dueDate = new Date(Date.now());

        const result = await infoApi.getContactInfo();
        if (result.isSuccess && result.data) {
            dueDate.setDate(dueDate.getDate() + result.data.defaultDueDays);
        }

        const invoice: IInvoice = {
            storno: StornoStateEnum.None,
            city: "",
            created: new Date(Date.now()),
            due: dueDate,
            exported: false,
            id: 0,
            invoiceNo: "",
            name: "",
            postalCode: "",
            street: "",
            ic: "",
            dic: "",
            year: 0,
            variabil: "",
            uskut: new Date(Date.now()),
            userId: "",
            userCreatedId: "",
            invoiceNoTemplate: "",
            maxPaidDate: undefined,
            items: [],
            number: 0,
            total: 0,
            totalPaid: 0,
            totalVat: 0,
            totalWidthVat: 0
        }
        setInvoice(invoice);
        setOriginalInvoice(invoice);
    }

    const saveInvoice = async (invoice?: IInvoice) => {
        if (invoice) {
            setIsLoading.setTrue();
            setStatusMessage(undefined);
            if (invoice.id === 0) {
                await createInvoice(invoice);
            }
            else {
                await updateInvoice(invoice);
            }
            setIsLoading.setFalse();
        }
    }

    const createInvoice = async (invoice: IInvoice) => {
        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.createInvoice({
            storno: StornoStateEnum.None,
            name: invoice.name,
            due: dateToStr(invoice.due as Date),
            postalCode: invoice.postalCode,
            userId: invoice.userId,
            uskut: dateToStr(invoice.uskut),
            city: invoice.city,
            description: "",
            dic: invoice.dic,
            ic: invoice.ic,
            street: invoice.street
        });
        if (result.isSuccess && result.data) {
            setStatusMessage({ message: "Faktura byla uložena", type: MessageBarType.success });
            await getInvoiceDetail(result.data);
            props.refresh && props.refresh();
            setIsChanged(false);
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
    }

    const updateInvoice = async (invoice: IInvoice) => {
        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.updateInvoice(invoice.id, {
            storno: StornoStateEnum.None,
            name: invoice.name,
            due: dateToStr(invoice.due),
            postalCode: invoice.postalCode,
            userId: invoice.userId,
            uskut: dateToStr(invoice.uskut),
            city: invoice.city,
            description: "",
            dic: invoice.dic,
            ic: invoice.ic,
            street: invoice.street
        });
        if (result.isSuccess && result.data) {
            setStatusMessage({ message: "Faktura byla uložena", type: MessageBarType.success });
            props.refresh && props.refresh();
            setIsChanged(false);
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
    }



    const deleteInvoiceItem = async (invoiceId: number) => {


    }

    const propertyChanged = (key: string, value: any) => {
        if (invoice) {
            setInvoice({ ...invoice, [key]: value });
        }
    }

    const onRenderFooter = (props?: IPanelProps, defaultRender?: (props?: IPanelProps) => JSX.Element | null) => {
        return null;
    }

    const findCustomer = async (query: string): Promise<IUserListItem[] | undefined> => {
        const invoiceApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const result = await invoiceApi.getUsers(query,"",false);
        if (result.isSuccess && result.data) {
            return result.data;
        }
        return undefined;
    }

    const setCustomer = async (customer: IUserListItem) => {
        const invoiceApi = Services.ServicesProvider.GetService<IAdminApi>(Services.AdminApi);
        const result = await invoiceApi.getUserInvoiceInfo(customer.id);
        if (result.isSuccess && result.data) {
            if (invoice) {
                setInvoice({
                    ...invoice,
                    userId: customer.id,
                    ic: result.data.ic,
                    dic: result.data.dic,
                    name: customer.company && customer.company?.length > 0 ? customer.company : customer.firstName + " " + customer.lastName,
                    street: result.data.billingStreet,
                    city: result.data.billingCity,
                    postalCode: result.data.billingPostalCode
                });
            }
        }
    }

    const sendViaEmail = async (invoice: number) => {
        setIsLoading.setTrue();

        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.sendInvoiceEmail([invoice]);
        if (result.isSuccess) {
            setStatusMessage({ message: "Faktura byla úspěšně odeslána emailem", type: MessageBarType.success });
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    const createStorno = async (invoiceId: number) => {

        setIsLoading.setTrue();

        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.createInvoiceStorno(invoiceId);
        if (result.isSuccess) {
            props.refresh && props.refresh();
            propertyChanged("storno", StornoStateEnum.Waiting);
            setStatusMessage({ message: "Požadavel na storno bylo úspěšně vytvořeno", type: MessageBarType.success });
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    const cancelStorno = async (invoiceId: number) => {
        setIsLoading.setTrue();
        const invoiceApi = Services.ServicesProvider.GetService<IInvoiceApi>(Services.InvoiceApi);
        const result = await invoiceApi.cancelInvoiceStorno(invoiceId);
        if (result.isSuccess) {
            props.refresh && props.refresh();
            propertyChanged("storno", StornoStateEnum.None);
            setStatusMessage({ message: "Požadavel na zrušení storna byl úspěšně vytvořen", type: MessageBarType.success });
        }
        else {
            setStatusMessage({ message: result.message, type: MessageBarType.error });
        }
        setIsLoading.setFalse();
    }

    return (
        <Panel
            isOpen={props.isDetailOpened}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            onRenderFooter={onRenderFooter }>
            <Stack>
                <Stack horizontal>
                    <Stack horizontalAlign='end' styles={{ root: { width: 80 } }}>
                        <FontIcon iconName="Event" className={iconClass} />
                    </Stack>
                    <Stack horizontal verticalAlign="center">
                        <Text styles={{ root: { fontWeight: 600 } }} variant="xLarge">
                            Detail faktury
                        </Text>
                    </Stack>
                </Stack>
                {props.isDetailOpened ?
                    <InvoiceDetailView
                        isChanged={isChanged}
                        saveInvoice={saveInvoice}
                        setCustomer={setCustomer}
                        findCustomer={findCustomer}
                        propertyChanged={propertyChanged}
                        invoice={invoice}
                        onDismiss={props.onDismiss}
                        isLoading={isLoading}
                        isReadOnly={isReadOnly}
                        reload={() => getInvoiceDetail(invoice ? invoice.id : 0)}
                        sendViaEmail={sendViaEmail}
                        createStorno={createStorno} 
                        cancelStorno={cancelStorno} 
                    />
                    : null}
                {statusMessage && statusMessage.message!="" ? <Stack styles={{ root: { paddingTop: 15 } }}> <MessageBar messageBarType={statusMessage.type}>{statusMessage?.message} </MessageBar></Stack> : null}
             </Stack>
        </Panel>
        )
}
